/**
 * Slice C.3 · HelpAnswer.
 * -----------------------
 * Reads canonical `presentation.data` mirrored from the domain
 * authority `help_centre.HELP_MANUALS`.
 *
 * Canonical fields (server → visible):
 *   • manual_title    → title line (bold)
 *   • subject_query   → "for '<subject>'"
 *   • manual_id       → shown as a small ID chip only when useful
 *   • target_path     → underpins the optional "Open manual" action
 *
 * Doctrine:
 *   • Never imports or calls `navigate()` directly.
 *   • Delegates navigation to the parent `onConfirmNavigate(target)`
 *     (same authority already ratified for `NavigateChoice`).
 *   • Renders the action button ONLY when the presentation carries
 *     a `primaryAction` with a target AND that target passes
 *     `isSafeInternalRoute`. Otherwise the sheet shows the matched
 *     manual title + subject with NO actionable button.
 *   • Dismiss button never navigates — it calls `onDismiss` only.
 *   • Opening the sheet never changes the URL.
 */
import { isSafeInternalRoute } from "../normalizePresentation";

export function HelpAnswer({ presentation, onConfirmNavigate, onDismiss }) {
  const d = presentation.data || {};
  const manualTitle  = typeof d.manual_title  === "string" ? d.manual_title  : null;
  const subjectQuery = typeof d.subject_query === "string" ? d.subject_query : null;
  const manualId     = typeof d.manual_id     === "string" ? d.manual_id     : null;

  // Primary action: only render when server proposed a target
  // AND the target passes the same authoritative safety check
  // used by NavigateChoice. Otherwise the button is not rendered
  // at all — never a dead destination.
  const proposedTarget = presentation.primaryAction?.target || null;
  const primaryLabel   = presentation.primaryAction?.label  || "Open manual";
  const targetIsSafe   = typeof proposedTarget === "string"
                          && isSafeInternalRoute(proposedTarget);
  const actionableTarget = targetIsSafe ? proposedTarget : null;

  const secondaryLabel = presentation.secondaryAction?.label || "Not now";

  return (
    <div data-testid="ui-response-help-answer" className="space-y-3">
      <p
        className="text-[14px] leading-relaxed text-zinc-100"
        data-testid="ui-help-narration"
      >
        {presentation.narration}
      </p>
      {(manualTitle || subjectQuery || manualId) && (
        <dl className="text-[13px] grid grid-cols-2 gap-x-3 gap-y-1 text-zinc-300">
          {manualTitle && (
            <>
              <dt className="text-zinc-500">Manual</dt>
              <dd data-testid="ui-help-manual-title">{manualTitle}</dd>
            </>
          )}
          {subjectQuery && (
            <>
              <dt className="text-zinc-500">Subject</dt>
              <dd data-testid="ui-help-subject-query">{subjectQuery}</dd>
            </>
          )}
          {manualId && (
            <>
              <dt className="text-zinc-500">ID</dt>
              <dd data-testid="ui-help-manual-id">{manualId}</dd>
            </>
          )}
        </dl>
      )}
      <div className="flex gap-2 pt-1">
        {/* Primary action rendered only when target is safe.
            Delegates navigation to the parent — never navigates
            in-renderer. */}
        {actionableTarget && (
          <button
            type="button"
            data-testid="ui-help-answer-confirm"
            onClick={() => onConfirmNavigate?.(actionableTarget)}
            className="inline-flex flex-1 items-center justify-center rounded-full bg-white text-zinc-900 px-4 py-2 text-[13px] font-semibold"
          >
            {primaryLabel}
          </button>
        )}
        {/* Dismiss is always available. Never navigates. */}
        <button
          type="button"
          data-testid="ui-help-answer-dismiss"
          onClick={() => onDismiss?.()}
          className="inline-flex items-center justify-center rounded-full border border-white/20 text-white px-4 py-2 text-[13px]"
        >
          {secondaryLabel}
        </button>
      </div>
    </div>
  );
}

export default HelpAnswer;
